import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clampValue, getCenteredPan, getFitZoom } from '../utils/geometry';

const DEFAULT_MIN_ZOOM = 0.1;
const DEFAULT_MAX_ZOOM = 4;

const useStageControls = ({
  viewportRef,
  artboardWidth,
  artboardHeight,
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  padding = 80,
}) => {
  const [zoom, setZoomState] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [autoFit, setAutoFit] = useState(true);
  const sizeRef = useRef({ width: 0, height: 0 });
  const panStartRef = useRef(null);
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  const applyFit = useCallback(() => {
    const { width, height } = sizeRef.current;
    if (!width || !height) return;
    const nextZoom = getFitZoom({
      viewportWidth: width,
      viewportHeight: height,
      artboardWidth,
      artboardHeight,
      padding,
      minZoom,
      maxZoom,
    });
    const nextPan = getCenteredPan({
      viewportWidth: width,
      viewportHeight: height,
      artboardWidth,
      artboardHeight,
      zoom: nextZoom,
    });
    setZoomState(nextZoom);
    setPan(nextPan);
  }, [artboardHeight, artboardWidth, maxZoom, minZoom, padding]);

  useLayoutEffect(() => {
    if (!viewportRef?.current) return undefined;
    const element = viewportRef.current;
    const updateSize = () => {
      sizeRef.current = {
        width: element.clientWidth,
        height: element.clientHeight,
      };
      if (autoFit) {
        applyFit();
      }
    };

    updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [applyFit, autoFit, viewportRef]);

  const setZoom = useCallback((value) => {
    setAutoFit(false);
    setZoomState(clampValue(value, minZoom, maxZoom));
  }, [maxZoom, minZoom]);

  const setZoomAtPoint = useCallback((nextZoom, point) => {
    const clampedZoom = clampValue(nextZoom, minZoom, maxZoom);
    const currentZoom = zoomRef.current;
    const currentPan = panRef.current;
    if (!point) {
      setZoom(clampedZoom);
      return;
    }
    const worldX = (point.x - currentPan.x) / currentZoom;
    const worldY = (point.y - currentPan.y) / currentZoom;
    const nextPan = {
      x: point.x - worldX * clampedZoom,
      y: point.y - worldY * clampedZoom,
    };
    setAutoFit(false);
    setZoomState(clampedZoom);
    setPan(nextPan);
  }, [maxZoom, minZoom, setZoom]);

  const setZoomAtCenter = useCallback((nextZoom) => {
    const { width, height } = sizeRef.current;
    if (!width || !height) {
      setZoom(nextZoom);
      return;
    }
    const centerPoint = { x: width / 2, y: height / 2 };
    setZoomAtPoint(nextZoom, centerPoint);
  }, [setZoom, setZoomAtPoint]);

  const zoomIn = useCallback(() => {
    setZoomAtCenter(zoomRef.current + 0.1);
  }, [setZoomAtCenter]);

  const zoomOut = useCallback(() => {
    setZoomAtCenter(zoomRef.current - 0.1);
  }, [setZoomAtCenter]);

  const fitToScreen = useCallback(() => {
    setAutoFit(true);
    applyFit();
  }, [applyFit]);

  const startPan = useCallback((event) => {
    const target = event.currentTarget;
    if (target?.setPointerCapture) {
      try {
        target.setPointerCapture(event.pointerId);
      } catch (error) {
        // Ignore capture errors on unsupported browsers.
      }
    }
    setAutoFit(false);
    panStartRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: panRef.current.x,
      originY: panRef.current.y,
    };
    setIsPanning(true);
  }, []);

  const updatePan = useCallback((event) => {
    if (!panStartRef.current) return;
    const { startX, startY, originX, originY } = panStartRef.current;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    setPan({
      x: originX + dx,
      y: originY + dy,
    });
  }, []);

  const endPan = useCallback((event) => {
    if (!panStartRef.current) return;
    const target = event.currentTarget;
    if (target?.releasePointerCapture) {
      try {
        target.releasePointerCapture(panStartRef.current.pointerId);
      } catch (error) {
        // Ignore capture errors on unsupported browsers.
      }
    }
    panStartRef.current = null;
    setIsPanning(false);
  }, []);

  const handleWheelZoom = useCallback((event) => {
    if (!viewportRef?.current) return;
    const rect = viewportRef.current.getBoundingClientRect();
    const point = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    const direction = event.deltaY > 0 ? 0.92 : 1.08;
    setZoomAtPoint(zoomRef.current * direction, point);
  }, [setZoomAtPoint, viewportRef]);

  const getViewportSize = useCallback(() => sizeRef.current, []);

  return {
    zoom,
    pan,
    isPanning,
    setZoom,
    setZoomAtPoint,
    setZoomAtCenter,
    zoomIn,
    zoomOut,
    fitToScreen,
    startPan,
    updatePan,
    endPan,
    handleWheelZoom,
    getViewportSize,
    setPan,
  };
};

export default useStageControls;
